// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { App, Button, InputNumber, Popconfirm, Space, Switch, Typography } from "antd";
import { cacheStats, clearCache, cacheCapBytes, setCacheCapBytes, autoDownloadEnabled, setAutoDownload } from "../mediaCache";
import { linkPreviewsEnabled, setLinkPreviews } from "../linkPreview";
import { isNativeApp } from "../server";

const MB = 1_000_000;

// MediaCachePanel surfaces the on-device media cache: how much is stored, the LRU cap,
// and a clear button. On the native app media lives in the device's Zalmoxis folder
// (device-managed), so we just explain that.
export function MediaCachePanel() {
  const { message } = App.useApp();
  const [stats, setStats] = useState<{ count: number; bytes: number; quota: number } | null>(null);
  const [capMB, setCapMB] = useState(Math.round(cacheCapBytes() / MB));
  const [auto, setAuto] = useState(autoDownloadEnabled());
  const [previews, setPreviews] = useState(linkPreviewsEnabled());

  const autoToggle = (
    <Space>
      <Switch checked={auto} onChange={(v) => { setAuto(v); setAutoDownload(v); }} />
      <Typography.Text>Automatically download incoming media</Typography.Text>
    </Space>
  );
  const previewToggle = (
    <Space align="start">
      <Switch checked={previews} onChange={(v) => { setPreviews(v); setLinkPreviews(v); }} />
      <div>
        <Typography.Text>Show link previews</Typography.Text>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
          Inline previews for image/video links and YouTube. Loading a preview reveals your IP to that
          site, so it's off by default. (Arbitrary web pages can't be previewed without a server.)
        </Typography.Paragraph>
      </div>
    </Space>
  );

  async function refresh() {
    setStats(await cacheStats());
  }
  useEffect(() => {
    if (!isNativeApp()) void refresh();
  }, []);

  if (isNativeApp()) {
    return (
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Media is saved to the <Typography.Text code>Zalmoxis</Typography.Text> folder on your device, so
          it stays available offline and even if the server removes the original. Manage it from your Files
          app.
        </Typography.Paragraph>
        {autoToggle}
        {previewToggle}
      </Space>
    );
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Media is kept on this device so it stays readable offline and even after the server removes the
        original. The oldest items are dropped first once the cache passes its limit.
      </Typography.Paragraph>

      {autoToggle}
      {previewToggle}

      <Typography.Text>
        Using{" "}
        <Typography.Text strong>
          {stats ? (stats.bytes / MB).toFixed(1) : "…"} MB
        </Typography.Text>{" "}
        across {stats?.count ?? "…"} item{stats?.count === 1 ? "" : "s"}
        {stats && stats.quota > 0 && <Typography.Text type="secondary"> · browser budget ~{Math.round(stats.quota / MB)} MB</Typography.Text>}
      </Typography.Text>

      <Space>
        <Typography.Text>Limit</Typography.Text>
        <InputNumber
          min={50}
          max={50_000}
          step={100}
          value={capMB}
          onChange={(v) => {
            const mb = Number(v) || 1000;
            setCapMB(mb);
            setCacheCapBytes(mb * MB);
          }}
          addonAfter="MB"
        />
      </Space>

      <Popconfirm title="Clear cached media on this device?" okText="Clear" okButtonProps={{ danger: true }} onConfirm={async () => { await clearCache(); await refresh(); message.success("Cached media cleared"); }}>
        <Button danger>Clear cache</Button>
      </Popconfirm>
    </Space>
  );
}
